import { ExtractedMedicalData, StageUpdate } from './types';

export interface WebSocketMessage {
  type: 'status' | 'result' | 'error';
  jobId: string;
  data: StageUpdate | ExtractedMedicalData | { message: string };
}

export interface ConnectionOptions {
  onOpen?: () => void;
  onMessage?: (message: WebSocketMessage) => void;
  onError?: (error: Event) => void;
  onClose?: (event: CloseEvent) => void;
  autoReconnect?: boolean;
  maxReconnectAttempts?: number;
  reconnectInterval?: number;
}

export class DocumentParserWebSocketClient {
  private websocket: WebSocket | null = null;
  private endpoint: string;
  private jobId: string;
  private userId: string;
  private connectionOptions: ConnectionOptions;
  private reconnectAttempts = 0;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private isReconnecting = false;

  constructor(
    endpoint: string,
    jobId: string,
    userId: string = 'frontend-user',
    options: ConnectionOptions = {},
  ) {
    this.endpoint = endpoint;
    this.jobId = jobId;
    this.userId = userId;
    this.connectionOptions = {
      autoReconnect: true,
      maxReconnectAttempts: 5,
      reconnectInterval: 2000,
      ...options,
    };
  }

  public connect(): void {
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      console.log('WebSocket is already connected');
      return;
    }

    // Clear any existing reconnect timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    try {
      // Construct the WebSocket URL with action parameter for explicit connection intent
      const wsUrl = `${this.endpoint}?action=$connect&userId=${this.userId}&jobId=${this.jobId}`;
      this.websocket = new WebSocket(wsUrl);

      this.websocket.onopen = event => {
        console.log('WebSocket connection established');
        this.reconnectAttempts = 0;
        this.isReconnecting = false;

        // Call the onOpen callback if provided
        if (this.connectionOptions.onOpen) {
          this.connectionOptions.onOpen();
        }

        // Send an 'initiate' message to associate this connection with the job
        this.sendMessage('initiate', { jobId: this.jobId });
      };

      this.websocket.onmessage = event => {
        try {
          const message = JSON.parse(event.data) as WebSocketMessage;

          // Call the onMessage callback if provided
          if (this.connectionOptions.onMessage) {
            this.connectionOptions.onMessage(message);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error, event.data);
        }
      };

      this.websocket.onerror = event => {
        console.error('WebSocket error:', event);

        // Call the onError callback if provided
        if (this.connectionOptions.onError) {
          this.connectionOptions.onError(event);
        }
      };

      this.websocket.onclose = event => {
        console.log('WebSocket connection closed', event.code, event.reason);

        // Call the onClose callback if provided
        if (this.connectionOptions.onClose) {
          this.connectionOptions.onClose(event);
        }

        // Attempt to reconnect if enabled
        if (
          this.connectionOptions.autoReconnect &&
          !this.isReconnecting &&
          this.reconnectAttempts < (this.connectionOptions.maxReconnectAttempts || 5)
        ) {
          this.attemptReconnect();
        }
      };
    } catch (error) {
      console.error('Error creating WebSocket connection', error);

      // Attempt to reconnect if enabled
      if (
        this.connectionOptions.autoReconnect &&
        !this.isReconnecting &&
        this.reconnectAttempts < (this.connectionOptions.maxReconnectAttempts || 5)
      ) {
        this.attemptReconnect();
      }
    }
  }

  public disconnect(): void {
    // Clear any pending reconnect attempts
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // Close the WebSocket connection if it exists
    if (this.websocket) {
      this.websocket.close(1000, 'Client disconnected');
      this.websocket = null;
    }
  }

  public sendMessage(action: string, data: any): boolean {
    if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
      console.warn('Cannot send message, WebSocket is not connected');
      return false;
    }

    try {
      const message = JSON.stringify({
        action,
        data,
      });
      this.websocket.send(message);
      return true;
    } catch (error) {
      console.error('Error sending WebSocket message', error);
      return false;
    }
  }

  public requestStatus(): boolean {
    return this.sendMessage('status', { jobId: this.jobId });
  }

  public isConnected(): boolean {
    return this.websocket !== null && this.websocket.readyState === WebSocket.OPEN;
  }

  private attemptReconnect(): void {
    this.isReconnecting = true;
    this.reconnectAttempts++;

    console.log(
      `Attempting to reconnect (${this.reconnectAttempts}/${this.connectionOptions.maxReconnectAttempts})...`,
    );

    // Use exponential backoff for reconnection attempts
    const delay =
      this.connectionOptions.reconnectInterval || 2000 * Math.pow(1.5, this.reconnectAttempts - 1);

    this.reconnectTimer = setTimeout(() => {
      this.connect();
    }, delay);
  }
}
